import { useTranslation } from "react-i18next";
import { useAtomValue } from "jotai";
import { FilePen, FileText, ListChecks, Repeat, RotateCcw, Trash2, Truck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ActionDataBo } from "@/types/tableAction";
import { userAtom } from "@/stores/authStore";

const dangerClassName = "border-red-600 text-red-600 hover:bg-red-50 hover:text-red-700";

export const DrawerFooter = ({
  isDeleted,
  isCustomQuotation,
  isBilled,
  onAction,
}: {
  isDeleted: boolean;
  isCustomQuotation: boolean;
  isBilled: boolean;
  onAction: (action: ActionDataBo["action"]) => void;
}) => {
  const { t } = useTranslation(["boExpeditions", "common"]);
  const user = useAtomValue(userAtom);
  const isSuperAdmin = user?.role === "SUPERADMIN";

  const primaryButtons = isDeleted
    ? isSuperAdmin
      ? [
          {
            label: t("expeditionDrawer.recover"),
            icon: RotateCcw,
            onClick: () => onAction("RECOVER"),
          },
        ]
      : []
    : [
        ...(isCustomQuotation && !isBilled && isSuperAdmin
          ? [
              {
                label: t("expeditionDrawer.editQuote"),
                icon: FilePen,
                onClick: () => onAction("QUOTE"),
              },
            ]
          : []),
        {
          label: t("expeditionDrawer.reExpedition"),
          icon: Repeat,
          onClick: () => onAction("RE_EXPEDITION"),
        },
        {
          label: t("expeditionDrawer.billReturn"),
          icon: FileText,
          onClick: () => onAction("RETURN"),
        },
        {
          label: t("expeditionDrawer.deliveryStatus"),
          icon: ListChecks,
          onClick: () => onAction("DELIVERY_STATUS"),
        },
        {
          label: t("expeditionDrawer.postponePickup"),
          icon: Truck,
          onClick: () => onAction("POSTPONE"),
        },
      ];

  const deleteButton = isDeleted
    ? isSuperAdmin
      ? {
          label: t("expeditionDrawer.permanentDelete"),
          onClick: () => onAction("PERMANENT_DELETE"),
        }
      : null
    : { label: t("expeditionDrawer.delete"), onClick: () => onAction("DELETE") };

  return (
    <div className="border-border flex w-full items-center justify-between gap-2 overflow-x-auto border-t bg-white p-5">
      <div className="flex items-center gap-2">
        {primaryButtons.map(({ label, icon: Icon, onClick }) => (
          <Button key={label} variant="outline" onClick={onClick}>
            <Icon className="size-4" />
            {label}
          </Button>
        ))}
      </div>
      {deleteButton && (
        <Button variant="outline" onClick={deleteButton.onClick} className={dangerClassName}>
          <Trash2 className="size-4" />
          {deleteButton.label}
        </Button>
      )}
    </div>
  );
};
